import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, guestToken, setCsrf } from "@/lib/api";
import { setFx } from "@/lib/format";
import { readLang, saveLang, translate, trField, trProduct } from "@/lib/i18n";

const Ctx = createContext(null);
const RV_KEY = "lc_recently_viewed";
const CCY_KEY = "ea_currency";
const DEFAULT_CONFIG = { store_name: "EA Italian Luxury Brands", base_currency: "ALL", reference_currency: "EUR", fx: { EUR: "98.50" }, checkout_enabled: false, payment_methods: ["cash_on_delivery", "card"], free_delivery_threshold: "15000" };
const EMPTY_NAV = { departments: [], audiences: [], brands: [], collections: [] };

const readCcy = () => {
  try {
    const v = localStorage.getItem(CCY_KEY);
    return v === "EUR" ? "EUR" : "ALL";
  } catch {
    return "ALL";
  }
};

export function ShopProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], item_count: 0, subtotal: "0.00", issues: [] });
  const [wishlist, setWishlist] = useState({ items: [], count: 0 });
  const [config, setConfig] = useState(null);
  const [nav, setNav] = useState(null);
  const [bagOpen, setBagOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [lang, setLangState] = useState(readLang);
  const [currencyMode, setCurrencyModeState] = useState(readCcy);

  const setLang = (l) => {
    setLangState(l);
    saveLang(l);
    document.documentElement.lang = l;
  };
  const setCurrencyMode = (c) => {
    setCurrencyModeState(c);
    try { localStorage.setItem(CCY_KEY, c); } catch { /* ignore */ }
  };
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const refreshCart = useCallback(async () => {
    try {
      const r = await api.get("/store/cart");
      setCart(r.data);
    } catch { /* keep previous */ }
  }, []);
  const refreshWishlist = useCallback(async () => {
    try {
      const r = await api.get("/store/wishlist");
      setWishlist(r.data);
    } catch { /* keep previous */ }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/customer/me");
      setCsrf("customer", r.data.csrf_token);
      setCustomer(r.data.customer);
    } catch {
      setCsrf("customer", null);
      setCustomer(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get("/store/config");
      setFx(r.data.fx);
      setConfig(r.data);
    } catch {
      setConfig(DEFAULT_CONFIG);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await loadMe();
      refreshCart();
      refreshWishlist();
    })();
    loadConfig();
    api.get("/store/navigation").then((r) => setNav(r.data)).catch(() => setNav(EMPTY_NAV));
  }, [loadMe, refreshCart, refreshWishlist, loadConfig]);

  const afterAuth = async (data) => {
    setCsrf("customer", data.csrf_token);
    setCustomer(data.customer);
    guestToken.clear(); // guest cart & wishlist were merged server-side
    await Promise.all([refreshCart(), refreshWishlist()]);
  };
  const login = async (email, password) => afterAuth((await api.post("/auth/customer/login", { email, password })).data);
  const register = async (payload) => afterAuth((await api.post("/auth/customer/register", payload)).data);
  const logout = async () => {
    try {
      await api.post("/auth/customer/logout");
    } finally {
      setCsrf("customer", null);
      setCustomer(null);
      setCart({ items: [], item_count: 0, subtotal: "0.00", issues: [] });
      setWishlist({ items: [], count: 0 });
    }
  };

  const addToCart = async (variantId, quantity = 1) => {
    const r = await api.post("/store/cart/items", { variant_id: variantId, quantity });
    setCart(r.data);
    return r.data;
  };
  const updateQty = async (lineId, quantity) => {
    try {
      const r = await api.patch(`/store/cart/items/${lineId}`, { quantity });
      setCart(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const removeLine = async (lineId) => {
    try {
      const r = await api.delete(`/store/cart/items/${lineId}`);
      setCart(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const isWished = (productId) => wishlist.items.some((i) => i.product_id === productId);
  const toggleWishlist = async (product) => {
    try {
      if (isWished(product.id)) {
        const r = await api.delete(`/store/wishlist/${product.id}`);
        setWishlist(r.data);
        toast(translate(lang, "removed_from_wishlist"));
      } else {
        const r = await api.post("/store/wishlist", { product_id: product.id });
        setWishlist(r.data);
        toast.success(translate(lang, "saved_to_wishlist"));
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pushRecentlyViewed = useCallback((id) => {
    try {
      const cur = JSON.parse(localStorage.getItem(RV_KEY) || "[]").filter((x) => x !== id);
      localStorage.setItem(RV_KEY, JSON.stringify([id, ...cur].slice(0, 12)));
    } catch { /* ignore */ }
  }, []);
  const recentlyViewed = () => {
    try {
      return JSON.parse(localStorage.getItem(RV_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const value = useMemo(() => ({
    customer, authLoading, login, register, logout, reloadMe: loadMe,
    cart, refreshCart, addToCart, updateQty, removeLine,
    wishlist, refreshWishlist, isWished, toggleWishlist,
    config: config || null, nav, bagOpen, setBagOpen, searchOpen, setSearchOpen, pushRecentlyViewed, recentlyViewed,
    lang, setLang, currencyMode, setCurrencyMode,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [customer, authLoading, cart, wishlist, config, nav, bagOpen, searchOpen, lang, currencyMode]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useShop = () => useContext(Ctx);

/** Translation helpers bound to the current language. */
export const useI18n = () => {
  const ctx = useContext(Ctx);
  const lang = ctx?.lang || "en";
  return useMemo(() => ({
    lang,
    t: (key, vars) => translate(lang, key, vars),
    tr: (obj, field = "name") => trField(lang, obj, field),
    tp: (p, field = "name") => trProduct(lang, p, field),
  }), [lang]);
};
